<!--
  =====================================================================poi fnpm
  HOME PAGE - MAIN LANDING PAGE
  =====================================================================
  
  Main landing page component that displays the company's homepage.
  
  FEATURES:
  - Loads dynamic page sections from store
  - Renders different section types using dynamic components
  - Initializes and manages hero section green bar visual effect
  - Shows loading and error states during data fetch
  
  LIFECYCLE:
  1. Component mounts → loads page content from store
  2. Waits for next tick to ensure DOM is updated
  3. Initializes hero green bar animation observer
  4. On unmount → cleans up observers and resets styles
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />
    <main class="homepage">

      <div v-if="state.loading" class="loading">
        <div class="dots-loader">
          <span class="dot dot-1"></span>
          <span class="dot dot-2"></span>
          <span class="dot dot-3"></span>
        </div>
      </div>
      
      <div v-else-if="state.error" class="error">
        Errore durante il caricamento della pagina.
      </div>

      <div v-else class="content">
        <component
          v-for="(section, index) in state.pageSections"
          :key="index"
          :is="getComponent(section.type)"
          :content="section.content"
        />
      </div>
    </main>
    <ArrowUp />  
    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, nextTick } from 'vue'
import { state, loadPageContent } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter from '@/components/sections/shared/SiteFooter.vue'

// ───────────────────────────────────────────────────────────────────
// HOMEPAGE-SPECIFIC SECTION COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SectionHero from '@/components/sections/home-page/SectionHero.vue'
import SectionTripleTitle from '@/components/sections/home-page/SectionTripleTitle.vue'
import SectionThreePanels from '@/components/sections/home-page/SectionThreePanels.vue'

// ───────────────────────────────────────────────────────────────────
// CROSS-SECTION COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SectionNewsCarousel from '@/components/sections/news/SectionNewsCarousel.vue'
import ArrowUp from '@/components/ArrowUp.vue'

// ───────────────────────────────────────────────────────────────────
// UTILITIES
// ───────────────────────────────────────────────────────────────────
import { setupHeroGreenBar, disposeHeroGreenBar } from '@/utils/heroGreenBar'

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE STATE
// ───────────────────────────────────────────────────────────────────

/** 
 * Stores cleanup function for hero green bar observer
 * Called on component unmount to prevent memory leaks
 */
let cleanupHeroGreenBar = () => {}

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Initialize page on component mount
 * 
 * Flow:
 * 1. Load page content from store (/home endpoint)
 * 2. Wait for nextTick to ensure hero section is in DOM
 * 3. Initialize hero green bar animation observer
 */
onMounted(async () => {
  // 1. Load page content from store
  await loadPageContent('/home')

  // 2. Wait for DOM update to have .section-hero element
  await nextTick()

  // 3. Initialize hero green bar animation
  cleanupHeroGreenBar = setupHeroGreenBar()
})

/**
 * Clean up resources on component unmount
 * 
 * - Stops IntersectionObserver for hero green bar
 * - Resets styles to prevent leaks
 */
onBeforeUnmount(() => {
  cleanupHeroGreenBar()       // Disconnect observer
  disposeHeroGreenBar()       // Reset global styles
})

// ───────────────────────────────────────────────────────────────────
// COMPONENT RESOLVER
// ───────────────────────────────────────────────────────────────────

/**
 * Maps section types to their corresponding Vue components
 * Used by dynamic component rendering based on content type
 * 
 * @param {string} type - The section type from page content
 * @returns {VueComponent} The component to render
 */
function getComponent(type) {
  switch (type) {
    case 'section-split':
      return SectionSplit
    case 'section-hero':
      return SectionHero
    case 'section-triple-title':
      return SectionTripleTitle
    case 'news-carousel':
      return SectionNewsCarousel
    case 'section-three-panels':
      return SectionThreePanels
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";
@import "@/assets/styles/partials/compat";
.homepage {
  padding: $space-1; 
}

.loading,
.error {
  text-align: center;
  padding: $space-2; 
}

.content {
  display: flex;
  flex-direction: column;
  @include flex-gap($space-2, column);

  @media (max-width: 768px) {
    @include flex-gap($space-1-5, column);
  }

  @media (max-width: 480px) {
    @include flex-gap($space-1, column);
  }
}

.homepage > .section-hero {
  margin-left: -$space-1;
  margin-right: -$space-1;
  margin-top: -$space-1;
}
</style>